import { useEffect, useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaginationBar } from "@/components/shared/PaginationBar";
import { formatIst } from "./shared/formatIst";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const MODE_BADGE = { UPI: "bg-sky-600", Cash: "bg-amber-500", "RTGS/NEFT": "bg-violet-600", "Imprest Bill": "bg-teal-500" };

// Read-only list of soft-deleted payment records (Director/Manager/Back Office).
export default function DeletedEntriesTab() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = (p = 1) => {
    setLoading(true);
    client
      .get("/payment-desk/deleted-entries", { params: { page: p } })
      .then((res) => {
        setRows(res.data.items);
        setPage(res.data.page);
        setTotalPages(res.data.total_pages);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, []);

  return (
    <div className="space-y-4" data-testid="deleted-entries-tab">
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="deleted-entries-table">
          <TableHeader>
            <TableRow>
              <TableHead>Mode</TableHead>
              <TableHead>Entry</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Attributed To</TableHead>
              <TableHead>Paid To</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status at Deletion</TableHead>
              <TableHead>Deleted By</TableHead>
              <TableHead>Deleted On</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400" data-testid="deleted-entries-empty">No deleted entries.</TableCell></TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} data-testid={`deleted-row-${r.id}`}>
                  <TableCell><Badge className={MODE_BADGE[r.record_type] || "bg-slate-500"}>{r.record_type}</Badge></TableCell>
                  <TableCell className="font-mono text-xs font-semibold">
                    {r.ref}
                    {r.created_by_name && <span className="block font-sans font-normal text-slate-400">by {r.created_by_name}</span>}
                  </TableCell>
                  <TableCell className="text-xs">{r.entity_name || "—"}</TableCell>
                  <TableCell className="text-xs">{r.site_label || r.equipment_label || "—"}</TableCell>
                  <TableCell className="text-xs">{r.payee_type}{r.payee_name ? ` — ${r.payee_name}` : ""}</TableCell>
                  <TableCell>{money(r.amount)}</TableCell>
                  <TableCell className="text-xs">{r.status_at_deletion || "—"}</TableCell>
                  <TableCell className="text-xs">{r.deleted_by_name}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{formatIst(r.deleted_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="deleted-entries-pagination" />
    </div>
  );
}
